import React, { useState, useEffect, useMemo } from 'react';
import { OI_SLIDER_MAX } from '../../../utils/liquidityRule';
import FilterSections from './FilterSections';
import { useStrategy } from '../../../contexts/StrategyContext';
import {
  AdvancedFilters,
  DistributionResponse,
  FilterSidebarProps,
  COVERED_CALL_DEFAULT_FILTERS
} from './types';
import DataDelayIndicator from '../../common/DataDelayIndicator';
import { PresetSelector } from '../../presets';
import { Preset } from '../../../types/presets';
import './FilterSidebar.css';

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  onChange,
  resultCount,
  isLoadingCount = false,
  activePresetId,
  onSelectPreset,
  onSavePreset,
  isPresetModified = false,
  basePresetId = null,
}) => {
  const { strategy } = useStrategy();  // BAB-33: lens-aware labels
  const isCsp = strategy === 'csp';
  // Distribution data for histograms
  const [distributionData, setDistributionData] = useState<DistributionResponse | null>(null);

  // Fetch distribution data on mount
  useEffect(() => {
    fetchDistributionData();
  }, []);

  const fetchDistributionData = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/stocks/screener/distribution`);
      const data = await response.json();
      if (data.status === 'success') {
        setDistributionData(data);
      }
    } catch (error) {
      console.error('Failed to fetch distribution data:', error);
    }
  };

  // Helper to check if arrays are equal
  const arraysEqual = (a?: string[], b?: string[]) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    return a.every(v => b.includes(v)) && b.every(v => a.includes(v));
  };

  // Count active filters (only those that differ from covered call defaults)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    // IV Rank - check if different from defaults
    if (filters.iv_rank_min !== undefined && filters.iv_rank_min !== (COVERED_CALL_DEFAULT_FILTERS.iv_rank_min ?? 0)) count++;
    if (filters.iv_rank_max !== undefined && filters.iv_rank_max !== (COVERED_CALL_DEFAULT_FILTERS.iv_rank_max ?? 100) && filters.iv_rank_max < 100) count++;
    // Annualized Yield - no default
    if (filters.annualized_yield_min !== undefined && filters.annualized_yield_min > 0) count++;
    if (filters.annualized_yield_max !== undefined && filters.annualized_yield_max < 100) count++;
    // Spread - check if different from default
    if (filters.spread_pct_min !== undefined && filters.spread_pct_min > 0) count++;
    if (filters.spread_pct_max !== undefined && filters.spread_pct_max !== (COVERED_CALL_DEFAULT_FILTERS.spread_pct_max ?? 30) && filters.spread_pct_max < 30) count++;
    // Market cap - check if different from default
    if (!arraysEqual(filters.market_cap_categories, COVERED_CALL_DEFAULT_FILTERS.market_cap_categories)) count++;
    // Sectors - no default
    if (filters.sectors && filters.sectors.length > 0) count++;
    // Fundamentals - no defaults
    if (filters.net_margin_min !== undefined && filters.net_margin_min > -50) count++;
    if (filters.net_margin_max !== undefined && filters.net_margin_max < 50) count++;
    if (filters.debt_equity_max !== undefined && filters.debt_equity_max < 5) count++;
    if (filters.dividend_yield_min !== undefined && filters.dividend_yield_min > 0) count++;
    if (filters.dividend_yield_max !== undefined && filters.dividend_yield_max < 15) count++;
    // Earnings - check if different from default
    if (filters.exclude_earnings_within_days !== undefined && filters.exclude_earnings_within_days !== COVERED_CALL_DEFAULT_FILTERS.exclude_earnings_within_days) count++;
    if (filters.earnings_within_days !== undefined && filters.earnings_within_days > 0) count++;
    // RSI - check if different from default
    if (filters.rsi_min !== undefined && filters.rsi_min !== (COVERED_CALL_DEFAULT_FILTERS.rsi_min ?? 0)) count++;
    if (filters.rsi_max !== undefined && filters.rsi_max !== (COVERED_CALL_DEFAULT_FILTERS.rsi_max ?? 100)) count++;
    // Trend signals - no default
    if (filters.trend_signals && filters.trend_signals.length > 0) count++;
    // New filters
    if (filters.slope_12w_min !== undefined && filters.slope_12w_min > -50) count++;
    if (filters.slope_12w_max !== undefined && filters.slope_12w_max < 50) count++;
    if (filters.macd_signals && filters.macd_signals.length > 0) count++;
    if (filters.setup_signals && filters.setup_signals.length > 0) count++;
    if (filters.open_interest_min !== undefined && filters.open_interest_min !== (COVERED_CALL_DEFAULT_FILTERS.open_interest_min ?? 0)) count++;
    if (filters.open_interest_max !== undefined && filters.open_interest_max < OI_SLIDER_MAX) count++;
    if (filters.min_dimension1_stars !== undefined) count++;
    if (filters.min_dimension2_stars !== undefined) count++;
    if (filters.baseline_pnl_min !== undefined && filters.baseline_pnl_min > -3) count++;
    if (filters.baseline_pnl_max !== undefined && filters.baseline_pnl_max < 4) count++;
    return count;
  }, [filters]);

  // Reset to default covered call filters
  const handleResetAll = () => {
    onChange({ ...COVERED_CALL_DEFAULT_FILTERS });
    // Also clear preset when resetting to defaults
    if (onSelectPreset) {
      onSelectPreset(null);
    }
  };

  // Update a filter value
  const updateFilter = (updates: Partial<AdvancedFilters>) => {
    onChange({ ...filters, ...updates });
    // Clear active preset when filters are manually changed
    if (onSelectPreset && activePresetId) {
      onSelectPreset(null);
    }
  };

  // Handle preset selection
  const handleSelectPreset = (preset: Preset | null) => {
    if (preset) {
      // Apply preset's screener filters
      const newFilters: AdvancedFilters = {
        iv_rank_min: preset.screener_filters.iv_rank_min,
        iv_rank_max: preset.screener_filters.iv_rank_max,
        annualized_yield_min: preset.screener_filters.annualized_yield_min,
        annualized_yield_max: preset.screener_filters.annualized_yield_max,
        spread_pct_min: preset.screener_filters.spread_pct_min,
        spread_pct_max: preset.screener_filters.spread_pct_max,
        market_cap_categories: preset.screener_filters.market_cap_categories,
        sectors: preset.screener_filters.sectors,
        net_margin_min: preset.screener_filters.net_margin_min,
        net_margin_max: preset.screener_filters.net_margin_max,
        debt_equity_max: preset.screener_filters.debt_equity_max,
        dividend_yield_min: preset.screener_filters.dividend_yield_min,
        dividend_yield_max: preset.screener_filters.dividend_yield_max,
        earnings_within_days: preset.screener_filters.earnings_within_days,
        exclude_earnings_within_days: preset.screener_filters.exclude_earnings_within_days,
        rsi_min: preset.screener_filters.rsi_min,
        rsi_max: preset.screener_filters.rsi_max,
        trend_signals: preset.screener_filters.trend_signals,
      };
      onChange(newFilters);
      if (onSelectPreset) {
        onSelectPreset(preset.id);
      }
    } else {
      // Clear filters when "Custom" is selected
      onChange({});
      if (onSelectPreset) {
        onSelectPreset(null);
      }
    }
  };

  return (
    <div className="filter-sidebar">
      {/* Header */}
      <div className="filter-sidebar-header">
        <h3>
          Filters
          {activeFilterCount > 0 && (
            <span className="filter-count-badge">{activeFilterCount}</span>
          )}
        </h3>
        {activeFilterCount > 0 && (
          <button
            className="filter-sidebar-reset"
            onClick={handleResetAll}
          >
            Reset
          </button>
        )}
      </div>

      {/* Preset Selector */}
      <div className="filter-sidebar-presets">
        <PresetSelector
          activePresetId={activePresetId ?? null}
          onSelectPreset={handleSelectPreset}
          onSavePreset={onSavePreset}
          showSaveButton={!!onSavePreset}
          isModified={isPresetModified}
          basePresetId={basePresetId}
        />
      </div>

      {/* Content: grouped like the stock card (Score → Income → Setup → Risk → Company → Market) */}
      <div className="filter-sidebar-content">
        <FilterSections filters={filters} update={updateFilter} distributionData={distributionData} isCsp={isCsp} />
      </div>

      {/* Footer */}
      <div className="filter-sidebar-footer">
        <div className="filter-sidebar-count">
          {isLoadingCount ? (
            'Loading...'
          ) : (
            <>
              <strong>{resultCount !== null ? resultCount.toLocaleString() : '...'}</strong> stocks match
            </>
          )}
        </div>
        <DataDelayIndicator className="filter-sidebar-delay" />
      </div>
    </div>
  );
};

export default FilterSidebar;
